import { observable, computed, action } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import { ServiceSelectionElements } from '~/components/Builder/Block/ServiceSelectionBlock/ServiceSelectionItemBlock';
import { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { IServiceSelectionItemBlockStyles } from '~/types/block-styles.types';
import { ServiceItemScope, DnDTypes, TBlock } from '~/types/builder.types';
import {
    ServiceSelectionItemBlockErrors,
    TServiceSelectionItemErrors,
    IErrorAdditionalProps,
    TServiceSelectionBlockInputValidations
} from '~/types/error.types';
import {
    TCropRatio,
    IService,
    ServicesItemsLayoutType
} from '~/types/model.types';
import {
    IServiceSelectionItemBlockResponse,
    ISessionConfig
} from '~/types/server.types';
import type { ServiceItemsContainerBlock } from '~/models/blocks/service-items-container-block.model';
import { falseNotZero } from '@honeybook/hbui/dist/components/HBMoneyInput/HBMoneyInput.utils';

type ServiceSelectionItemBlockDataForServer = ReturnType<
    ServiceItemBlock['getDataForServer']
> &
    IServiceSelectionItemBlockResponse;

export class ServiceSelectionItemBlock extends ServiceItemBlock {
    type: 'service_selection_item_block';
    @observable styles: IServiceSelectionItemBlockStyles;
    @observable invoice_service_item: IService;
    @observable allow_custom_quantity: boolean; // when referencing this, check first if you need to use isCustomQuantity method -
    // it considers the SS being 'view-only' and then custom quantity is not allowed.
    @observable min_quantity: number;
    @observable max_quantity: number;
    @observable session_config_id: string | null;
    @observable session_config: ISessionConfig | null;
    @observable session_config_hash: string | null;

    constructor(
        block: IServiceSelectionItemBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.invoice_service_item = block.invoice_service_item;
        this.allow_custom_quantity = block.allow_custom_quantity;
        this.min_quantity = block.min_quantity;
        this.max_quantity = block.max_quantity;
        this.session_config_id = block.session_config_id;
        this.session_config = block.session_config;
    }

    get isDeletable() {
        return true;
    }

    @computed
    get selectButtonText() {
        return this.serviceSelectionBlock.styles.select_button_text || 'Select';
    }

    @computed
    get label() {
        return this.isInPackage ? 'Service sub-item' : 'Service item';
    }

    @computed
    get attributable() {
        return true;
    }

    @computed
    get shouldDisplayCornerRadiusSlider() {
        return (
            this.serviceSelectionBlock.services_layout ===
            ServicesItemsLayoutType.Rows
        );
    }

    @computed
    get isCustomQuantity() {
        return (
            this.allow_custom_quantity &&
            this.serviceSelectionBlock.hasSelection
        );
    }

    @computed
    get shouldExpandOnSelect() {
        return (
            !this.isInPackage &&
            isEmpty(this.rootStore.builderUiStore.isolatedBlockEditorState)
        );
    }

    @computed
    get subItems() {
        return this.childBlocks as ServiceSelectionItemBlock[];
    }

    get isServiceSelectionItemBlock() {
        return true;
    }

    @computed
    get isInvalidCustomQuantity() {
        return this.blockInputValidations[
            ServiceSelectionItemBlockErrors.InvalidCustomQuantity
        ];
    }

    @computed
    get isMissingTitle() {
        return this.blockInputValidations[
            ServiceSelectionItemBlockErrors.MissingTitle
        ];
    }

    @computed
    get isMissingSessionConfig() {
        return (
            !this.isInPackage &&
            this.serviceSelectionBlock.isSessionSelection &&
            !this.session_config_id
        );
    }

    @computed
    get isMissingTitleOnInvoiceInstance() {
        return this.invoice_service_item && !this.invoice_service_item.title;
    }

    getErrors(
        additionalProps: IErrorAdditionalProps = {}
    ): TServiceSelectionItemErrors {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }

        const isDontCountSubItemsErrors =
            additionalProps?.isDontCountSubItemsErrors;
        const isSubItemsModalScope =
            additionalProps?.scope ===
            ServiceItemScope.ServiceSelectionSubItemsModal;
        const isInvoiceScope =
            additionalProps?.scope === ServiceItemScope.Invoice;
        const { isColumnLayout } = this.serviceSelectionBlock;

        const errors: TServiceSelectionItemErrors = { ...super.errors };

        // Always validate missing title, even for templates
        if (!isInvoiceScope && this.isMissingTitle) {
            errors.itemTitle = true;
        }

        // For templates, only validate missing title and return early
        if (selectedFlow.is_template) {
            if (!isEmpty(errors)) {
                errors.hasErrors = true;
            }
            return errors;
        }

        // For non-templates, continue with comprehensive validation
        if (isInvoiceScope) {
            if (
                !this.isMissingTitle && // we first check that there is a title in the SS block, because it reflects in the invoice.
                this.isMissingTitleOnInvoiceInstance
            ) {
                errors.invoiceTitle = true;
            }
        } else {
            if (this.isInvalidCustomQuantity) {
                errors.itemQuantity = true;
            }

            if (this.isMissingSessionConfig) {
                errors.sessionConfig = true;
            }

            // if we're in service selection and the layout is column, report errors for the hidden sub items.
            // don't report this for page error count.
            if (
                this.isPackage &&
                isColumnLayout &&
                !isSubItemsModalScope &&
                !isDontCountSubItemsErrors
            ) {
                if (this.subItems.some(subItem => subItem.isMissingTitle)) {
                    errors.subItemTitle = true;
                }
                if (
                    this.subItems.some(
                        subItem => subItem.isInvalidCustomQuantity
                    )
                ) {
                    errors.subItemQuantity = true;
                }
            }
        }

        if (!isEmpty(errors)) {
            errors.hasErrors = true;
        }
        return errors;
    }

    @computed
    get blockInputValidations(): TServiceSelectionBlockInputValidations {
        const validations = {};
        if (
            this.isCustomQuantity &&
            this.max_quantity !== null &&
            this.max_quantity !== Infinity &&
            this.min_quantity > this.max_quantity
        ) {
            validations[ServiceSelectionItemBlockErrors.InvalidCustomQuantity] =
                true;
        }
        if (!this.service_item?.title) {
            validations[ServiceSelectionItemBlockErrors.MissingTitle] = true;
        }
        return validations;
    }

    @computed
    get serviceSelectionBlock(): ServiceSelectionBlock {
        let { parent } = this;

        while (parent && parent.type !== 'service_selection_block') {
            parent = parent.parent;
        }

        return parent as ServiceSelectionBlock;
    }

    @computed
    get serviceItemsContainerBlock(): ServiceItemsContainerBlock {
        let { parent } = this;

        while (parent && parent.type !== 'service_items_container_block') {
            parent = parent.parent;
        }

        return parent as ServiceItemsContainerBlock;
    }

    @computed
    get isSubItemsModalOpenForPackage() {
        const packageId = this.isInPackage ? this.parent_id : this.block_id;
        return this.rootStore.builderUiStore
            .openServiceSelectionSubItemsModalMap[packageId];
    }

    @computed
    get isIsolatedEditorOpenForPackage() {
        const packageId = this.isInPackage ? this.parent_id : this.block_id;
        return this.rootStore.builderUiStore.isolatedBlockEditorState?.blocks?.some(
            block => block.block_id === packageId
        );
    }

    @computed
    get allowedCropRatios(): { default: TCropRatio; types: TCropRatio[] } {
        return this.isColumnLayout &&
            !this.isSubItemsModalOpenForPackage &&
            !this.isIsolatedEditorOpenForPackage
            ? {
                  default: '4:3',
                  types: ['4:3', 'Circle']
              }
            : {
                  default: '1:1',
                  types: ['1:1', 'Circle']
              };
    }

    dndAcceptedTypes(additionalProps: any) {
        if (
            this.isPackage &&
            additionalProps?.scope ===
                ServiceItemScope.ServiceSelectionSubItemsModal
        ) {
            return null;
        }
        if (additionalProps?.scope === ServiceItemScope.Invoice) {
            return [DnDTypes.ServiceSelectionItem];
        }
        return [
            DnDTypes.ServiceSelectionItem,
            DnDTypes.ServiceItemInPackage,
            DnDTypes.ServiceItem,
            DnDTypes.Package
        ];
    }

    getDataForInvoice() {
        return this.invoice_service_item || this.service_item;
    }

    @computed
    get selectedQuantity(): number {
        if (!this.isCustomQuantity) {
            const parsedValue = parseFloat(String(this.service_item.quantity));
            return Number.isNaN(parsedValue) ? 1 : parsedValue;
        }

        return this.connectedVariable?.value?.quantity || this.min_quantity;
    }

    @computed
    get totalPrice() {
        return (
            (this.selectedQuantity ?? 1) * this.service_item.price_per_unit || 0
        );
    }

    @computed
    get isColumnLayout() {
        return (
            this.serviceSelectionBlock.services_layout !==
            ServicesItemsLayoutType.Rows
        );
    }

    @computed
    get isSomeItemNotLimited() {
        return [this, ...this.subItems].some(
            serviceItem =>
                serviceItem.isCustomQuantity && !serviceItem.max_quantity
        );
    }

    @computed
    get modelParentBlock() {
        return this.isInPackage ? this.parent : this.serviceSelectionBlock;
    }

    isDraggable(customProps?: { scope?: ServiceItemScope }): boolean {
        // Disable d&d for service selection in column view and a single service item
        // and for invoice service items
        // todo: verify that we can't drag after paid!

        if (customProps?.scope === ServiceItemScope.Invoice) {
            return false;
        }

        const isOnlyChild = this.modelParentBlock?.childBlocks?.length === 1;
        if (isOnlyChild) {
            return false;
        }

        const isInColumnsView =
            !customProps?.scope &&
            this.serviceSelectionBlock?.services_layout &&
            this.serviceSelectionBlock?.services_layout !==
                ServicesItemsLayoutType.Rows;

        return !isInColumnsView;
    }

    @computed
    get serviceMaxPrice() {
        if (this.isSomeItemNotLimited) {
            return Infinity;
        }

        return [this, ...this.subItems].reduce((totalMaxPrice, item) => {
            return (
                totalMaxPrice +
                (item.isCustomQuantity
                    ? item.max_quantity * item.service_item.price_per_unit
                    : item.service_item.price)
            );
        }, 0);
    }

    @computed
    get serviceMinPrice() {
        return [this, ...this.subItems].reduce((totalMaxPrice, item) => {
            let quantity;
            if (item.isCustomQuantity) {
                quantity = item.min_quantity;
            } else {
                quantity = falseNotZero(item.service_item.quantity)
                    ? 1
                    : item.service_item.quantity;
            }
            return totalMaxPrice + quantity * item.service_item.price_per_unit;
        }, 0);
    }

    @computed
    get isSubItem() {
        return this.parent instanceof ServiceSelectionItemBlock;
    }

    @computed
    get isServiceSelected() {
        const potentialSelectedBlock = this.isSubItem
            ? (this.parent as ServiceSelectionItemBlock)
            : this;
        return this.serviceSelectionBlock.selectedServiceBlocks?.includes(
            potentialSelectedBlock
        );
    }

    @computed
    get isSessionSelection() {
        return (
            !this.isInPackage && this.serviceSelectionBlock.isSessionSelection
        );
    }

    isSiblingElementSelected(element: ServiceSelectionElements) {
        const { selectedInternalElement } = this.rootStore.builderUiStore;

        const allServiceSelectionSiblingBlockItems = this.serviceSelectionBlock
            .allDescendants()
            .filter(
                blk =>
                    blk instanceof ServiceSelectionItemBlock &&
                    blk.isInPackage === this.isInPackage
            );

        return allServiceSelectionSiblingBlockItems.some(
            blk => selectedInternalElement[blk.block_id] === element
        );
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }

        if (block.min_quantity) {
            this.min_quantity = block.min_quantity;
        }

        if (block.max_quantity) {
            this.max_quantity = block.max_quantity;
        }
    }

    canBeParentOf(block: TBlock) {
        return (
            this.isPackage &&
            block instanceof ServiceSelectionItemBlock &&
            !block.isPackage
        );
    }

    getDataForServer(): ServiceSelectionItemBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'service_selection_item_block',
            invoice_service_item: this.invoice_service_item,
            allow_custom_quantity: this.allow_custom_quantity,
            min_quantity: this.min_quantity,
            max_quantity: this.max_quantity,
            session_config_id: this.session_config_id,
            session_config: this.session_config,
            session_config_hash: this.session_config_hash
        };
    }
}
